import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@exhousing/design-system";
import { useState } from "react";
import { toast } from "sonner";

import { useCreateCostAdjustment } from "@/hooks/api/useCostAdjustments";
import type { components } from "@/types/api";

type Contract = components["schemas"]["ContractResponse"];

const today = () => new Date().toISOString().slice(0, 10);

/** Records a dated change to the cost advances (the annual settlement). Costs are
 *  locked from silent edits once active, so this event is the controlled path —
 *  it updates the contract and the bill, and lands in the timeline. */
export function CostAdjustmentCard({ contract }: { contract: Contract }) {
  const create = useCreateCostAdjustment(contract.id);
  const [open, setOpen] = useState(false);
  const [service, setService] = useState(contract.service_costs?.toString() ?? "");
  const [gwl, setGwl] = useState(contract.gwl_costs?.toString() ?? "");
  const [other, setOther] = useState(contract.other_costs?.toString() ?? "");
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [note, setNote] = useState("");

  const num = (s: string): number | undefined => (s === "" ? undefined : Number(s));

  const submit = () => {
    create.mutate(
      {
        effective_date: effectiveDate,
        note: note || null,
        service_costs: num(service),
        gwl_costs: num(gwl),
        other_costs: num(other),
      },
      {
        onSuccess: () => {
          toast.success("Cost adjustment recorded — the bill is updated");
          setOpen(false);
          setNote("");
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Service costs</CardTitle>
        {!open && (
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            Adjust
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Service costs are advances the tenant signed for, so they change only through a
          dated adjustment (e.g. the annual settlement) — which updates the contract and
          the bill, and is recorded on the timeline.
        </p>

        {open && (
          <div className="space-y-3 border-t border-border/60 pt-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label htmlFor="ca-service">Service costs</Label>
                <Input
                  id="ca-service"
                  type="number"
                  value={service}
                  onChange={(e) => setService(e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ca-gwl">Gas / water / elec.</Label>
                <Input
                  id="ca-gwl"
                  type="number"
                  value={gwl}
                  onChange={(e) => setGwl(e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ca-other">Other</Label>
                <Input
                  id="ca-other"
                  type="number"
                  value={other}
                  onChange={(e) => setOther(e.target.value)}
                  className="h-9"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="ca-date">Effective date</Label>
                <Input
                  id="ca-date"
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ca-note">Note</Label>
                <Input
                  id="ca-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. 2026 settlement"
                  className="h-9"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button size="sm" disabled={create.isPending} onClick={submit}>
                {create.isPending ? "Recording…" : "Record adjustment"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
